import { useState } from "react";
import LiveCallsSection from "./LiveCallsSection";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { MoreVertical, Bot, Trash2, Edit, Power, ShoppingBag, History, Undo2 } from "lucide-react";
import { toast } from "sonner";
import React from "react";
import {
  useGetAiAgentsQuery,
  useDeleteAiAgentMutation,
  useKeepAiAgentMutation,
  useToggleAiAgentMutation,
} from "@/store/rest_api";
import { AiAgent } from "@/model/interface/AiAgent";
import { apiErrorMessage } from "@/util/apiError";

export default function AiAgentsPage() {
  const navigate = useNavigate();
  const { data: agents, isLoading, error } = useGetAiAgentsQuery();
  const accessDenied = !!error && (error as { status?: number }).status === 402;
  const [deleteAgent] = useDeleteAiAgentMutation();
  const [toggleAgent] = useToggleAiAgentMutation();
  const [keepAgent] = useKeepAiAgentMutation();
  const [agentToDelete, setAgentToDelete] = useState<AiAgent | null>(null);

  const formatPaidThrough = (paidThrough?: string | null) => {
    if (!paidThrough) {
      return "";
    }
    return new Date(`${paidThrough}T00:00:00`).toLocaleDateString("en-US", { month: "long", day: "numeric" });
  };

  const handleDelete = async () => {
    if (!agentToDelete) {
      return;
    }
    try {
      const result = await deleteAgent(agentToDelete.id).unwrap();
      if (result.lifecycle === "REMOVAL_SCHEDULED") {
        toast.success(`Agent keeps working until ${formatPaidThrough(result.paidThrough)}, then it is removed`);
      } else {
        toast.success("Agent removed");
      }
      setAgentToDelete(null);
    } catch (error: unknown) {
      toast.error(apiErrorMessage(error, "Failed to remove agent"));
    }
  };

  const handleKeep = async (agent: AiAgent) => {
    try {
      await keepAgent(agent.id).unwrap();
      toast.success("Agent kept. It will renew as usual");
    } catch (error: unknown) {
      toast.error(apiErrorMessage(error, "Failed to keep agent"));
    }
  };

  const handleToggle = async (agent: AiAgent) => {
    try {
      const result = await toggleAgent(agent.id).unwrap();
      if (result.status === "ACTIVE") {
        toast.success("Agent activated");
      } else if (result.paidThrough) {
        toast.success(`Agent deactivated. Switching it back on before ${formatPaidThrough(result.paidThrough)} is free`);
      } else {
        toast.success("Agent deactivated");
      }
    } catch (error: unknown) {
      toast.error(apiErrorMessage(error, "Failed to toggle agent"));
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading agents...</div>
      </div>
    );
  }

  if (error && !accessDenied) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-destructive">Failed to load agents</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">AI Agents</h1>
          <p className="text-muted-foreground">Manage your AI voice agents for inbound calls</p>
        </div>
        {!accessDenied && (
          <Button onClick={() => navigate("/ai-marketplace")}>
            <ShoppingBag className="mr-2 h-4 w-4" />
            Browse Marketplace
          </Button>
        )}
      </div>

      {/* Renders nothing unless a call is actually live. */}
      {!accessDenied && <LiveCallsSection />}

      {accessDenied ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Bot className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">AI Agents is a Pro feature</h3>
            <p className="text-muted-foreground text-center mb-4 max-w-md">
              Upgrade to the Pro plan to deploy AI voice agents that answer inbound calls and run outbound
              campaigns for you.
            </p>
            <Button onClick={() => navigate("/manage-organisation/plans")}>Upgrade to Pro</Button>
          </CardContent>
        </Card>
      ) : !agents || agents.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Bot className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No AI agents yet</h3>
            <p className="text-muted-foreground text-center mb-4 max-w-md">
              Browse the marketplace to find and acquire AI agents that can handle your inbound calls automatically.
            </p>
            <Button onClick={() => navigate("/ai-marketplace")}>
              <ShoppingBag className="mr-2 h-4 w-4" />
              Browse Marketplace
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Your Agents</CardTitle>
            <CardDescription>
              {agents.length} {agents.length === 1 ? "agent" : "agents"} configured
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Agent</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Options</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {agents.map((agent) => (
                  <TableRow key={agent.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-cyan-500 text-white">
                          <Bot className="h-4 w-4" />
                        </div>
                        <div className="font-medium">{agent.name}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={agent.status === "ACTIVE"}
                          onCheckedChange={() => handleToggle(agent)}
                        />
                        <Badge variant={agent.status === "ACTIVE" ? "default" : "secondary"}>
                          {agent.status === "ACTIVE" ? "Active" : "Inactive"}
                        </Badge>
                        {agent.lifecycle === "REMOVAL_SCHEDULED" && (
                          <Badge variant="outline">Ends {formatPaidThrough(agent.paidThrough)}</Badge>
                        )}
                      </div>
                      {agent.lifecycle !== "REMOVAL_SCHEDULED" && agent.status !== "ACTIVE" && agent.paidThrough && (
                        <p className="pt-1 text-xs text-muted-foreground">
                          Paid until {formatPaidThrough(agent.paidThrough)}. Switching it back on before then is free.
                        </p>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/ai-agents/${agent.id}`)}>
                            <Edit className="mr-2 h-4 w-4" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate(`/ai-agents/${agent.id}/calls`)}>
                            <History className="mr-2 h-4 w-4" />
                            Call History
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleToggle(agent)}>
                            <Power className="mr-2 h-4 w-4" />
                            {agent.status === "ACTIVE" ? "Deactivate" : "Activate"}
                          </DropdownMenuItem>
                          {agent.lifecycle === "REMOVAL_SCHEDULED" ? (
                            <DropdownMenuItem onClick={() => handleKeep(agent)}>
                              <Undo2 className="mr-2 h-4 w-4" />
                              Keep agent
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem onClick={() => setAgentToDelete(agent)} className="text-destructive">
                              <Trash2 className="mr-2 h-4 w-4" />
                              Remove
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <AlertDialog open={!!agentToDelete} onOpenChange={() => setAgentToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Agent</AlertDialogTitle>
            <AlertDialogDescription>
              "{agentToDelete?.name}" keeps working until the end of the period you have already paid for. After that
              it is removed for good and cannot be renewed. There is no refund for the current period, and you can
              undo this with "Keep agent" any time before that date.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
